/**
 * BrandingPreview Component
 *
 * Live preview of tenant branding configuration showing a mini sidebar,
 * color swatches, favicon preview, and browser tab mockup.
 *
 * Extracted from TenantsPage.tsx inline JSX (WQ-088).
 *
 * @module features/tenants/components/BrandingPreview
 */

import React from 'react';

export interface BrandingPreviewProps {
  primaryColor: string;
  secondaryColor: string;
  logoUrl?: string;
  faviconUrl?: string;
  tenantName: string;
  appName?: string;
}

const BrandingPreview: React.FC<BrandingPreviewProps> = ({
  primaryColor,
  secondaryColor,
  logoUrl,
  faviconUrl,
  tenantName,
  appName,
}) => {
  const displayName = appName || tenantName || 'Tenant App';

  return (
    <div style={{
      marginTop: '20px',
      padding: '16px',
      background: 'rgba(255,255,255,0.03)',
      borderRadius: '12px',
      border: '1px solid var(--border-secondary)',
    }}>
      <label style={{ display: 'block', marginBottom: '12px', fontWeight: 500 }}>
        Live Preview
      </label>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '200px 1fr',
        gap: '16px',
      }}>
        {/* Mini Sidebar Preview */}
        <div style={{
          background: 'linear-gradient(180deg, rgba(15,15,25,0.98) 0%, rgba(20,20,35,0.95) 100%)',
          borderRadius: '8px',
          padding: '12px',
          minHeight: '200px',
          border: '1px solid rgba(255,255,255,0.1)',
        }}>
          {/* Logo/App Name Area */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px',
            marginBottom: '12px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo preview"
                style={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'contain',
                  borderRadius: '4px',
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div style={{
                width: '32px',
                height: '32px',
                background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                color: 'white',
              }}>
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
            <span style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'white',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {displayName}
            </span>
          </div>

          {/* Mock Navigation Items */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {['Dashboard', 'Users', 'Settings'].map((item, idx) => (
              <div
                key={item}
                style={{
                  padding: '8px 10px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: idx === 0 ? 'white' : 'rgba(255,255,255,0.6)',
                  background: idx === 0 ? `${primaryColor}20` : 'transparent',
                  borderLeft: idx === 0 ? `2px solid ${primaryColor}` : '2px solid transparent',
                  cursor: 'pointer',
                }}
              >
                {item}
              </div>
            ))}
          </div>

          {/* Mock Button */}
          <div style={{ marginTop: '16px', padding: '0 8px' }}>
            <div style={{
              padding: '8px 12px',
              background: primaryColor,
              borderRadius: '6px',
              fontSize: '11px',
              color: 'white',
              textAlign: 'center',
              fontWeight: 500,
            }}>
              Primary Action
            </div>
          </div>
        </div>

        {/* Preview Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Color Swatches */}
          <div>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
              Color Palette
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '6px',
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  background: primaryColor,
                  border: '1px solid rgba(255,255,255,0.2)',
                }} />
                <span style={{ fontSize: '11px' }}>Primary</span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '6px',
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  background: secondaryColor,
                  border: '1px solid rgba(255,255,255,0.2)',
                }} />
                <span style={{ fontSize: '11px' }}>Secondary</span>
              </div>
            </div>
          </div>

          {/* Favicon Preview */}
          <div>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
              Favicon
            </span>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '6px',
              width: 'fit-content',
            }}>
              {faviconUrl ? (
                <img
                  src={faviconUrl}
                  alt="Favicon preview"
                  style={{ width: '16px', height: '16px' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/vite.svg';
                  }}
                />
              ) : (
                <div style={{
                  width: '16px',
                  height: '16px',
                  background: primaryColor,
                  borderRadius: '2px',
                }} />
              )}
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {faviconUrl ? 'Custom favicon' : 'Default favicon'}
              </span>
            </div>
          </div>

          {/* Browser Tab Preview */}
          <div>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
              Browser Tab
            </span>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              background: 'rgba(60,60,70,0.8)',
              borderRadius: '8px 8px 0 0',
              width: 'fit-content',
              maxWidth: '200px',
            }}>
              {faviconUrl ? (
                <img
                  src={faviconUrl}
                  alt=""
                  style={{ width: '14px', height: '14px' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div style={{
                  width: '14px',
                  height: '14px',
                  background: primaryColor,
                  borderRadius: '2px',
                }} />
              )}
              <span style={{
                fontSize: '11px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {displayName}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingPreview;
